import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import ClassroomCard from 'components/@common/Cards/ClassroomCard';
import LicenseInformationCard from 'components/@common/LicenseInformationCard';
import { format } from 'date-fns';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { ProductWithCurriculum } from 'types';
import {
  Maybe,
  OrganizationRole,
  UserClassroomEnrollment,
  UserOrganization
} from 'types/schema-generated-types';
import { getId } from 'utilities/product-utils';
import { filterPublicOrgAssociationRoles } from 'utilities/organization-util';

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: 0,
    paddingBottom: theme.spacing(3),
    gridRowGap: theme.spacing(1)
  },
  bolded: {
    fontWeight: 500
  },
  sectionContainer: {
    marginTop: theme.spacing(3)
  },
  introTextInformation: {},
  licenseCardContainer: {
    maxWidth: 700,
    display: 'grid',
    gridGap: 8,
    gridAutoRows: '1fr',
    gridAutoFlow: 'dense'
  },
  organizationRolesContainer: {
    marginTop: theme.spacing(2) / 4
  },
  productsItemsContainer: {
    marginTop: theme.spacing()
  },
  classesGrid: {
    marginTop: theme.spacing(1),
    display: 'grid',
    gridColumnGap: theme.spacing(1),
    gridRowGap: theme.spacing(1)
  }
}));

interface UserInformationBodyProps {
  pin?: string;
  email?: string;
  lastActive?: string;
  organizationAssociations: UserOrganization[];
  licensedProducts: ProductWithCurriculum[];
  classroomEnrollments: Maybe<UserClassroomEnrollment>[];
}

const UserInformationBody: React.FC<UserInformationBodyProps> = ({
  pin = '',
  email = '',
  organizationAssociations,
  lastActive,
  licensedProducts,
  classroomEnrollments
}) => {
  const classes = useStyles();
  const history = useHistory();

  const { t } = useTranslation('UserInformationBody');

  const handleClassroomCardClick = useCallback(
    classroomId => (): void => {
      history.push(`/admin/classes-students/${classroomId}`);
      window.location.reload();
    },
    [history]
  );

  return (
    <Grid container direction="column" wrap="nowrap" className={classes.root}>
      <Grid item>
        <Typography className={classes.bolded}>{t('Educator PIN', { ns: '@common' })}</Typography>
        <Typography>{pin}</Typography>
      </Grid>
      <Grid item>
        <Typography className={classes.bolded}>{t('Email Address', { ns: '@common' })}</Typography>
        <Typography>{email}</Typography>
      </Grid>
      {lastActive && (
        <Grid item>
          <Typography className={classes.bolded}>{t('Last Activity')}</Typography>
          <Typography>{format(new Date(lastActive), 'MMMM d, yyyy')}</Typography>
        </Grid>
      )}
      <Grid item className={classes.sectionContainer}>
        <Typography variant="h4">{t('Organization & Roles')}</Typography>
        <Grid
          container
          direction="column"
          spacing={1}
          className={classes.organizationRolesContainer}
        >
          {organizationAssociations?.map(
            // eslint-disable-next-line react/display-name
            ({ organization, organizationRoles }: UserOrganization) => (
              <Grid item>
                {filterPublicOrgAssociationRoles(organizationRoles).map(
                  ({ displayName }: OrganizationRole) => (
                    <Typography key={`${organization.name}${displayName}`}>
                      {organization.name} - {displayName}
                    </Typography>
                  )
                )}
              </Grid>
            )
          )}
        </Grid>
      </Grid>
      {!!licensedProducts?.length && (
        <Grid item className={classes.sectionContainer}>
          <Typography variant="h4">{t('Products', { ns: '@common' })}</Typography>
          <div className={classes.licenseCardContainer}>
            {licensedProducts.map(({ product, productCurriculum }) => (
              <LicenseInformationCard
                key={`license_${getId(product, productCurriculum)}`}
                product={product}
                productCurriculum={productCurriculum}
              />
            ))}
          </div>
        </Grid>
      )}
      {!!classroomEnrollments?.length && (
        <Grid item className={classes.sectionContainer}>
          <Typography variant="h4">{t('Classes', { ns: '@common' })}</Typography>
          <div className={classes.classesGrid}>
            {(classroomEnrollments as Array<UserClassroomEnrollment>).map(
              // eslint-disable-next-line react/display-name
              ({ classroom }: UserClassroomEnrollment) => (
                <ClassroomCard
                  key={`classroom_${classroom.id}`}
                  classroom={classroom}
                  onClick={handleClassroomCardClick(classroom.id)}
                />
              )
            )}
          </div>
        </Grid>
      )}
    </Grid>
  );
};

export default UserInformationBody;
